import React, {forwardRef} from 'react'
import styled from 'styled-components'
import Box, {BoxProps} from '../Box'
import {themeGet} from '../../utils/theme-get'
import {useRefObjectAsForwardedRef} from '../../hooks'
import {ArrowPlacement, RuleStepLabelProps} from './types'

const StyledArrow = styled(Box)<RuleStepLabelArrowProps>`
  position: absolute;
  background-color: ${({isSelected}) =>
    isSelected
      ? themeGet('colors.background.selected.bold.default')
      : themeGet('colors.background.neutral.bold.default')};

  height: 100%;
  &[data-placement='left'] {
    clip-path: polygon(0 50%, 10% 100%, 40% 100%, 60% 100%, 90% 100%, 100% 50%, 90% 0, 60% 0, 40% 0, 10% 0);

    transform: rotateY(180deg);
    left: -8px;
    width: 100%;
  }

  &[data-placement='right'][data-condition='if'] {
    clip-path: polygon(0 50%, 10% 100%, 40% 100%, 60% 100%, 90% 100%, 100% 50%, 90% 0, 60% 0, 40% 0, 10% 0);

    right: -8px;
    width: 100%;
  }
  &[data-placement='right'][data-condition='then'] {
    /** leaving as a reference */
    /* clip-path: polygon(0 50%, 90% 100%, 100% 50%, 90% 0); */
    /* right: -5px; */
    /* right: -5px;
    width: 100%; */
    /* clip-path: polygon(0% 50%, 85% 100%, 100% 50%, 85% 0); */

    clip-path: polygon(0% 0%, 100% 50%, 0% 100%);
    transform: rotate(0deg);
    right: -9px;
    width: 10px;
    height: 100%;
  }
`

const StyledStepLabel = styled(Box)<RuleStepLabelProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: ${themeGet('space.1')} ${themeGet('space.2')};
  background-color: ${({isSelected}) =>
    isSelected
      ? themeGet('colors.background.selected.bold.default')
      : themeGet('colors.background.neutral.bold.default')};
  color: ${themeGet('colors.text.inverse')};
  font-weight: ${themeGet('fontWeights.semibold')};
  font-size: ${themeGet('fontSizes.2')};
  text-transform: uppercase;
  border-radius: ${({condition}) => (condition === 'when' ? themeGet('radii.1') : '')};
  user-select: none;

  &:hover {
    background-color: ${({isSelected}) =>
      isSelected
        ? themeGet('colors.background.selected.bold.hovered')
        : themeGet('colors.background.neutral.bold.hovered')};
  }

  & > span {
    z-index: 1;
  }
`

const StyledStepLabelWrapper = styled(Box)<BoxProps & {isSelected?: boolean}>`
  display: flex;
  position: relative;
  &:hover ${StyledArrow} {
    background-color: ${({isSelected}) =>
      isSelected
        ? themeGet('colors.background.selected.bold.hovered')
        : themeGet('colors.background.neutral.bold.hovered')};
  }
  &:hover ${StyledStepLabel} {
    background-color: ${({isSelected}) =>
      isSelected
        ? themeGet('colors.background.selected.bold.hovered')
        : themeGet('colors.background.neutral.bold.hovered')};
  }
`

type RuleStepLabelArrowProps = {
  placement?: ArrowPlacement
  isSelected?: boolean
  isCurrentStep?: boolean
  condition?: 'if' | 'when' | 'then'
} & BoxProps

const RuleStepLabelArrow = ({placement, condition, isSelected, ...props}: RuleStepLabelArrowProps) => {
  return <StyledArrow data-placement={placement} isSelected={isSelected} data-condition={condition} {...props} />
}

const RuleStepLabel = forwardRef(
  (
    {
      label,
      description,
      condition,
      children,
      isSelected: isSelectedProp,
      isCompletedStep,
      testId,
      ...props
    }: RuleStepLabelProps & BoxProps,
    forwardedRef
  ) => {
    const containerRef = React.useRef<HTMLDivElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, containerRef)

    const labelTestId = `${testId}-step-label`
    const wrapperTestId = `${testId}-step-label-wrapper`

    const isActive = isSelectedProp || isCompletedStep
    const shouldRender = !!label || !!description
    return shouldRender ? (
      <StyledStepLabelWrapper
        ref={containerRef}
        sx={{display: 'flex', position: 'relative', width: 'fit-content'}}
        data-testid={testId ? labelTestId : undefined}
        isSelected={isActive}
      >
        {condition === 'if' ? (
          <RuleStepLabelArrow condition={condition} placement="left" isSelected={isActive} />
        ) : null}

        <StyledStepLabel
          isSelected={isActive}
          label={label}
          condition={condition}
          data-testid={testId ? labelTestId : undefined}
        >
          <span>{label}</span>
        </StyledStepLabel>
        {condition === 'if' ? <RuleStepLabelArrow isSelected={isActive} condition="if" placement="right" /> : null}
        {condition === 'then' ? <RuleStepLabelArrow isSelected={isActive} condition="then" placement="right" /> : null}
      </StyledStepLabelWrapper>
    ) : null
  }
)

RuleStepLabel.displayName = 'RuleStepLabel'
export default RuleStepLabel
